/**
 * component--market-reconcile.js
 * ------------------------------------------------------------------
 * When a customer switches markets, remove any cart lines that are not
 * published to the newly selected market's catalog, tell them why, and
 * refresh the AJAX drawer so the cart reflects server truth.
 *
 * Why this exists: Shopify enforces per-market availability at *display*
 * time (can't add) and at *checkout* (blocks + "empty cart" dead end),
 * but does NOT proactively purge an already-added line from the stored
 * cart when a shopper switches markets in the storefront. This closes
 * that gap on the client so they never reach the checkout wall.
 *
 * Safety model:
 *   - Removal is FAIL-CLOSED: on any Storefront API error we remove
 *     nothing, so a network blip never destroys a cart.
 *   - Eligibility keys on product VISIBILITY in context (null node), not
 *     availableForSale — so out-of-stock-but-region-valid items are kept.
 *
 * This is a UX layer. Keep Shopify's native checkout block (and/or a
 * checkout validation function) as the server-side floor.
 *
 * Setup (see chat notes):
 *   1. Inject window.CODEAGE_MARKET via Liquid (country + storefront token).
 *   2. Include this file: {{ 'component--market-reconcile.js' | asset_url }}
 * ------------------------------------------------------------------
 */
(function () {
  "use strict";

  var CONFIG = {
    // Storefront API version. Bump as you upgrade; any version supported
    // at time of use is fine.
    apiVersion: "2026-01",
    // sessionStorage keys
    switchFlag: "ca:marketSwitchPending",
    sessionSeenFlag: "ca:marketReconcileRan",
    // Flip to true while testing to see what the module decides.
    debug: true
  };

  var cfg = window.CODEAGE_MARKET || {};
  var COUNTRY = cfg.country || null;            // e.g. "US", "DE", "GB"
  var TOKEN = cfg.storefrontToken || null;      // public Storefront access token

  var inFlight = false;

  function log() {
    if (!CONFIG.debug) return;
    try { console.log.apply(console, ["[market-reconcile]"].concat([].slice.call(arguments))); } catch (_) {}
  }

  function setFlag(key) { try { sessionStorage.setItem(key, "1"); } catch (_) {} }
  function hasFlag(key) { try { return sessionStorage.getItem(key) === "1"; } catch (_) { return false; } }
  function clearFlag(key) { try { sessionStorage.removeItem(key); } catch (_) {} }

  // --- Trigger: mark that a market switch is happening -------------------
  // Redundant listeners so it fires regardless of how the localization form
  // is submitted (button click vs jQuery .submit()). Setting the flag is
  // idempotent, so firing more than once is harmless.
  function markSwitch() {
    setFlag(CONFIG.switchFlag);
    log("switch marked");
  }

  document.addEventListener("click", function (e) {
    var btn = e.target && e.target.closest && e.target.closest(".js-localization-form-submit");
    if (btn) markSwitch();
  }, true);

  document.addEventListener("submit", function (e) {
    var f = e.target;
    if (!f) return;
    var isLocalizationForm =
      f.id === "localization_form" ||
      (f.matches && f.matches("form[action*='/localization']"));
    if (isLocalizationForm) markSwitch();
  }, true);

  // --- Eligibility check -------------------------------------------------
  // Returns the set of handles that are NOT available in the given country.
  // Resolves to null on any failure (caller then removes nothing).
  function findIneligibleHandles(handles, country) {
    if (!handles.length) return Promise.resolve([]);
    if (!TOKEN) { log("no storefront token configured"); return Promise.resolve(null); }

    var fields = handles
      .map(function (h, i) { return "l" + i + ": product(handle: " + JSON.stringify(h) + ") { id availableForSale }"; })
      .join("\n");

    var query =
      "query Reconcile($country: CountryCode!) @inContext(country: $country) {\n" +
      fields +
      "\n}";

    return fetch("/api/" + CONFIG.apiVersion + "/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": TOKEN
      },
      body: JSON.stringify({ query: query, variables: { country: country } })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (!res || !res.data) { log("no data from storefront api", res); return null; }
        return handles.filter(function (h, i) {
          var node = res.data["l" + i];
          // PRIMARY signal: product not visible in this market's catalog -> null.
          return node === null;

          // ALTERNATIVE (only if your store returns a non-null node for
          // catalog-excluded products). Uncomment to also treat unavailable
          // as ineligible — note this WILL remove out-of-stock-but-valid items:
          // return node === null || node.availableForSale === false;
        });
      })
      .catch(function (err) { log("storefront api error", err); return null; });
  }

  // --- Removal -----------------------------------------------------------
  // Serialized: concurrent /cart/change.js calls can race.
  function removeLineKeys(keys) {
    var chain = Promise.resolve();
    keys.forEach(function (key) {
      chain = chain.then(function () {
        return fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      });
    });
    return chain;
  }

  // --- Toast -------------------------------------------------------------
  function showToast(titles) {
    var n = titles.length;
    var msg = n === 1
      ? "We removed \u201C" + titles[0] + "\u201D \u2014 it isn\u2019t available in your region."
      : "We removed " + n + " items not available in your region.";

    var el = document.createElement("div");
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.style.cssText = [
      "position:fixed",
      "left:50%",
      "bottom:24px",
      "transform:translateX(-50%)",
      "max-width:420px",
      "width:calc(100% - 32px)",
      "box-sizing:border-box",
      "background:#111",
      "color:#fff",
      "padding:14px 18px",
      "border-radius:8px",
      "font-size:14px",
      "line-height:1.45",
      "z-index:100000",
      "box-shadow:0 6px 24px rgba(0,0,0,.2)",
      "opacity:0",
      "transition:opacity .25s ease"
    ].join(";");
    el.textContent = msg;
    document.body.appendChild(el);

    requestAnimationFrame(function () { el.style.opacity = "1"; });
    setTimeout(function () {
      el.style.opacity = "0";
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 300);
    }, 5000);
  }

  // --- Drawer refresh (rebuilds from a fresh /cart.js) -------------------
  function refreshDrawer() {
    if (window.theme && theme.Drawer && typeof theme.Drawer.cartReloadPageRefresh === "function") {
      theme.Drawer.cartReloadPageRefresh();
    }
  }

  // --- Main --------------------------------------------------------------
  function reconcile(country) {
    if (inFlight) return;
    if (!country) { log("no country in context; skipping"); return; }
    inFlight = true;
    log("reconciling for", country);

    fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;

        var handles = cart.items
          .map(function (i) { return i.handle; })
          .filter(function (h, i, arr) { return h && arr.indexOf(h) === i; });

        return findIneligibleHandles(handles, country).then(function (ineligible) {
          if (ineligible === null) { log("eligibility check failed; removing nothing"); return; }
          if (!ineligible.length) { log("all items eligible"); return; }

          var linesToRemove = cart.items.filter(function (i) {
            return ineligible.indexOf(i.handle) !== -1;
          });
          var keys = linesToRemove.map(function (i) { return i.key; });
          var titles = linesToRemove
            .map(function (i) { return i.product_title; })
            .filter(function (t, i, arr) { return arr.indexOf(t) === i; });

          log("removing", keys);
          return removeLineKeys(keys).then(function () {
            //showToast(titles);
            refreshDrawer();
          });
        });
      })
      .catch(function (err) { log("reconcile error", err); })
      .finally(function () { inFlight = false; });
  }

  // --- Boot --------------------------------------------------------------
  function boot() {
    var pending = hasFlag(CONFIG.switchFlag);

    // OPTIONAL: also reconcile once per session on first load, to catch a
    // returning shopper whose persisted cart has an item that's ineligible
    // in their current context (no in-session switch happened). The native
    // checkout block covers this case for compliance regardless; this just
    // improves the UX. Uncomment the next two lines to enable:
    //
    var firstOfSession = !hasFlag(CONFIG.sessionSeenFlag);
    pending = pending || firstOfSession;

    setFlag(CONFIG.sessionSeenFlag);

    if (!pending) return;
    clearFlag(CONFIG.switchFlag);
    reconcile(COUNTRY);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();